import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { combineLatest, Observable, shareReplay } from 'rxjs';
import { EntData, EntTransit } from '@bazis/shared/models/srv.types';
import { BazisFormService } from '@bazis/form/services/form.service';
import { map, tap } from 'rxjs/operators';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { BazisMediaQueryService } from '@bazis/shared/services/media-query.service';
import { BazisConfigurationService } from '@bazis/configuration.service';

@UntilDestroy()
@Component({
    selector: 'bazis-entity-transits',
    templateUrl: './entity-transits.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisEntityTransitsComponent implements OnChanges {
    @Input()
    transits = [];

    @Input()
    signContext;

    @Input()
    transitMap: {
        [index: string]: EntTransit;
    };

    @Input()
    canTransit$: Observable<boolean>;

    @Input()
    hasChanges$: Observable<boolean>;

    @Input()
    actionElement: 'button' | 'link' = 'button';

    @Input()
    saveButtonSettings;

    @Input()
    hideRestricted = false;

    @Input()
    processTransitingTitleKey?: string;

    @Input()
    isSchemaValidateRedefine = {};

    @Output()
    save = new EventEmitter();

    transits$;

    successTransits$;

    failTransits$;

    hiddenTransits$;

    icons;

    constructor(
        public formService: BazisFormService,
        public entityService: BazisEntityService,
        public mqSrv: BazisMediaQueryService,
        private configurationService: BazisConfigurationService,
    ) {
        this.icons = this.configurationService.controlIcons;
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateTransits();
    }

    protected _getMainTransit(group) {
        return group[0];
    }

    updateTransits() {
        if (!this.transits) return;
        const transits = this.transits.map((group) =>
            this.entityService.getEntity$(
                'statusy.transit',
                encodeURIComponent(this._getMainTransit(group)),
            ),
        );

        this.transits$ = combineLatest(transits).pipe(
            map((transitEntities: EntData[]) =>
                transitEntities.map((transitEntity) => transitEntity?.$snapshot),
            ),
            shareReplay(),
        );

        ['success', 'fail', 'hidden'].forEach((transitType) => {
            this[`${transitType}Transits$`] = this.transits$.pipe(
                map((transitSnapshots: any[]) => {
                    const snapshots = [];
                    if (!this.transits) return snapshots;
                    this.transits.forEach((group) => {
                        const mainTransit = this._getMainTransit(group);

                        const mainSnapshot = transitSnapshots.find(
                            (transit) => transit.id === mainTransit,
                        );

                        if (mainSnapshot.transit_type === transitType) {
                            snapshots.push({
                                main: {
                                    ...this.transitMap[mainTransit],
                                    id: mainTransit,
                                    snapshot: mainSnapshot,
                                },
                                group: group.map((transitItem) => {
                                    return {
                                        ...this.transitMap[transitItem],
                                        id: transitItem,
                                    };
                                }),
                            });
                        }
                    });
                    return snapshots;
                }),
            );
        });
    }

    transitEntity(group) {
        if (this.formService.transiting._) return;
        this.formService.transitEntity(
            group.map((transitItem) => {
                return {
                    transit: transitItem.id,
                    url: transitItem.url,
                    payload: transitItem.payload,
                    hint: transitItem.hint,
                };
            }),
        );
    }
}
